import { Button, Card, Container, Typography } from "@material-ui/core";
import DropOffManageRacksAndBins from "@saleor/dropOff/components/DropOffDispatch/DropOffManageRacksAndBins";
import DropOffHomeLocationAndTaskArea from "@saleor/dropOff/components/DropOffHomeLocation/DropOffHomeLocationAndTaskArea";
import DropOffManageTaskAreas from "@saleor/dropOff/components/DropOffTaskArea/DropOffManageTaskAreas";
import { useTemplateUpdate } from "@saleor/dropOff/mutation";
import {
  useDropOffMallQuery,
  useGetAvailableTemplatesQuery,
  useGetTemplateQuery
} from "@saleor/dropOff/queries";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import {
  dropOffEditPath,
  dropOffListPath,
  DropOffListUrlQueryParams,
  dropOffViewPageUrl
} from "@saleor/dropOff/url";
import { TabValuesEnum } from "@saleor/dropOff/utils/filters";
import {
  TemplateAction,
  userCanManageTaskAreas
} from "@saleor/dropOff/utils/handlers";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { TemplateStatus, TemplateStatusEnum } from "@saleor/types/globalTypes";
import { handleGraphQLErrors } from "@saleor/utils/handlers/errorHandler";
import { stringifyQs } from "@saleor/utils/urls";
import React from "react";
import { FormattedMessage } from "react-intl";
import urlJoin from "url-join";

interface DropOffEditTemplateProps {
  params: DropOffListUrlQueryParams;
  id: string;
}

const DropOffEditTemplate: React.FC<DropOffEditTemplateProps> = props => {
  const { params, id } = props;

  const classes = useTemplateStyles(props);
  const navigate = useNavigator();
  const notify = useNotifier();
  const user = useUser();

  const [currentStep, setCurrentStep] = React.useState(params?.step);

  React.useEffect(() => {
    setCurrentStep(params?.step);
  }, [params?.step]);

  const { data: template } = useGetTemplateQuery({
    displayLoader: true,
    variables: {
      first: 100,
      id
    }
  });

  const { data: mall } = useDropOffMallQuery({
    displayLoader: true,
    skip: !params?.mall || params?.mall.length === 0,
    variables: {
      id: params?.mall
    }
  });

  const { data: templates } = useGetAvailableTemplatesQuery({
    displayLoader: true,
    variables: {
      filter: { mall: mall?.mall?.id },
      first: 100
    }
  });

  const handleSuccess = () => {
    notify({
      status: "success",
      text: "All changes saved"
    });

    if (!params?.mall || params?.mall.length === 0) {
      navigate(dropOffViewPageUrl());
    } else {
      navigate(
        urlJoin(
          dropOffListPath,
          "?" +
            stringifyQs({
              mall: params?.mall,
              tab: TabValuesEnum.TEMPLATES
            })
        )
      );
    }
  };

  const [finalizeInactiveTemplate] = useTemplateUpdate({
    onCompleted: e => {
      if (e.templateUpdate.templateError.length === 0) {
        handleSuccess();
      } else {
        notify({
          status: "error",
          text: e.templateUpdate.templateError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const clickDone = () => {
    let allHaveTaskAreas = true;

    const homeLocations = template?.template?.dropOffLocations?.edges?.map(
      edge => edge.node
    );
    homeLocations.forEach(homeLoc => {
      if (homeLoc?.taskAreas.length === 0) {
        allHaveTaskAreas = false;
      }
    });

    const templateList = templates?.templates?.edges.map(edge => edge.node);

    const nonDraftTemplates = templateList.filter(
      template => template.status !== TemplateStatus.DRAFT
    );

    if (
      template?.template?.status === TemplateStatus.ACTIVE ||
      template?.template?.status === TemplateStatus.INACTIVE
    ) {
      handleSuccess();
    } else if (
      homeLocations.length > 0 &&
      allHaveTaskAreas &&
      nonDraftTemplates.length < 10
    ) {
      finalizeInactiveTemplate({
        variables: {
          id: template?.template?.id,
          input: {
            mall: mall?.mall?.id,
            name: template?.template?.name,
            status: TemplateStatusEnum.INACTIVE
          }
        }
      });
    }
  };

  const handleBackForEditFromStep2 = (templateId, mallId) => {
    navigate(
      urlJoin(
        dropOffEditPath(templateId),
        "?" +
          stringifyQs({
            mall: mallId,
            step: 1
          })
      )
    );
  };

  const handleNextForEditToStep3 = (templateId, mallId) => {
    navigate(
      urlJoin(
        dropOffEditPath(templateId),
        "?" +
          stringifyQs({
            mall: mallId,
            step: 3
          })
      )
    );
  };

  const handleBackForEditFromStep3 = (templateId, mallId) => {
    navigate(
      urlJoin(
        dropOffEditPath(templateId),
        "?" +
          stringifyQs({
            mall: mallId,
            step: 2
          })
      )
    );
  };

  return (
    <Container>
      <div>
        <div className={classes.headerSection}>
          <div className={classes.headerLeft}>
            <div className={classes.headerTitleMall}>
              <h5 className={classes.headerTitle}>
                {template?.template?.name}
              </h5>
              <Typography color="primary" className={classes.mallTitle}>
                {mall?.mall?.name}
              </Typography>
            </div>
          </div>

          <div className={classes.headerLeft}>
            <Button
              size="large"
              className={classes.btnTopCreateNewTemplate}
              color="primary"
              variant="contained"
              onClick={() => clickDone()}
            >
              <FormattedMessage defaultMessage="Done" description="button" />
            </Button>
          </div>
        </div>

        <Card className={classes.dropOffCard} style={{ padding: "40px 0px" }}>
          {(!currentStep || currentStep.toString() === "1") && (
            <DropOffHomeLocationAndTaskArea
              mallId={mall?.mall?.id}
              templateId={template?.template?.id}
              templateAction={TemplateAction.EDIT}
              editHomeLocation={
                template?.template?.dropOffLocations?.edges?.find(
                  node => node.node.defaultDropOffLocation === true
                )?.node
              }
            />
          )}

          {!!currentStep && currentStep.toString() === "2" && (
            <DropOffManageRacksAndBins
              key={template?.template?.id}
              templateId={template?.template?.id}
              nextStep={() => {
                handleNextForEditToStep3(
                  template?.template?.id,
                  mall?.mall?.id
                );
              }}
              onBack={() => {
                handleBackForEditFromStep2(
                  template?.template?.id,
                  mall?.mall?.id
                );
              }}
              templateAction={TemplateAction.EDIT}
            />
          )}

          {!!currentStep && currentStep.toString() === "3" && (
            <DropOffManageTaskAreas
              disableRename={!userCanManageTaskAreas(user?.user)}
              disableAddRemove={!userCanManageTaskAreas(user?.user)}
              mallId={mall?.mall?.id}
              templateId={template?.template?.id}
              templateAction={TemplateAction.EDIT}
              clickBackForEditTemplate={handleBackForEditFromStep3}
            />
          )}
        </Card>
      </div>
    </Container>
  );
};

export default DropOffEditTemplate;
